// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The metric. This file is where chunkforge either tells the truth or does not,
 * so the reasoning is written down rather than assumed.
 *
 * ── Why there has to be a mark ───────────────────────────────────────────────
 * A hit rate is a score against ground truth, and with no labels there is no
 * ground truth. Every label-free proxy that suggests itself — the margin between
 * rank 1 and rank 2, agreement between two embedders, rank stability across
 * strategies — measures the confidence of the retriever, not its correctness,
 * and each is highest exactly when a retriever is confidently wrong. So
 * chunkforge asks for one thing per query: click the passage that should have
 * come back. Until you do, it shows the ranking moving and claims nothing.
 *
 * ── Why the mark is a span of the SOURCE, not a chunk ────────────────────────
 * Because a chunk only exists inside one chunking. A character range in the
 * document exists identically under every strategy and every size, so one mark
 * scores all of them. That is the entire reason a comparison is possible.
 *
 * ── Why a hit is BINARY overlap ──────────────────────────────────────────────
 * Not "what fraction of the marked span came back". People mark sloppily —
 * a whole paragraph when they meant a sentence — and measured against that
 * sloppiness a fractional score moves by −0.26 while binary overlap moves by
 * +0.02 to +0.04. A metric that punishes the user for the size of their
 * selection is measuring the selection, not the chunking.
 *
 * ── Why k is DERIVED and not fixed ───────────────────────────────────────────
 * This is the correction that makes the whole comparison valid. At a fixed k,
 * bigger chunks retrieve more text for free: top-10 is 1,270 tokens at 128 and
 * 54,080 at 8,192, so "recall goes up with chunk size" is arithmetic rather than
 * a finding. Instead the user sets a CONTEXT BUDGET — the tokens they are
 * willing to paste into their model — and k = floor(budget / chunk size). Every
 * setting is then compared at the same price, which is the only comparison worth
 * making.
 *
 * ── Why a zero-skill floor is drawn ──────────────────────────────────────────
 * At 64 tokens a budget of 4,096 retrieves 64 chunks, and if the corpus only has
 * 170 that is a third of it — a score of 1.0 there is arithmetic, not retrieval.
 * The floor is exactly what random selection would score, computed in closed
 * form, and when the top k covers the whole corpus the comparison is disabled
 * rather than reported.
 *
 * Prior art for span-level ground truth is Smith and Troynikov, "Evaluating
 * Chunking Strategies for Retrieval", Chroma Technical Report, 3 July 2024.
 * Their Recall, Precision, IoU and Precision_Omega need pre-annotated relevant
 * excerpts, which this tool deliberately does not ask for. This is a different,
 * simpler measurement, and it does not borrow their names for its numbers.
 */

import type { IndexedDoc, ChunkSpec } from './split.ts';
import { tokensIn } from './split.ts';

export interface AnswerSpan {
  docId: number;
  start: number;
  end: number;
}

export interface QuerySpec {
  id: string;
  text: string;
  /** Empty until the user marks the passage. Unmarked queries are never scored. */
  spans: AnswerSpan[];
}

export interface Hit {
  chunk: number;
  score: number;
  /** True when this chunk overlaps a marked span by at least one character. */
  isHit: boolean;
}

export interface QueryScore {
  queryId: string;
  marked: boolean;
  hits: Hit[];
  /** True when at least one of the retrieved chunks overlaps a marked span. */
  retrieved: boolean;
  /** 1-based rank of the first chunk that overlaps a marked span. */
  firstHitRank: number | null;
  retrievedTokens: number;
  /** How many chunks the ranking was drawn from — rank means nothing without it. */
  chunkCount: number;
  /** How many chunks the marked span touches at all. */
  reachableChunks: number;
  /** What pure chance would score here, in [0, 1]. */
  chanceLevel: number;
}

export interface RunScore {
  strategy: string;
  size: number;
  overlap: number;
  /** How many results were retrieved, derived from the budget. */
  k: number;
  chunkCount: number;
  /** Chunks that exceed the size because they had no separator left inside. */
  oversized: number;
  medianTokens: number;
  perQuery: QueryScore[];
  markedCount: number;
  /** Marked queries whose passage came back. The numerator; markedCount is the denominator. */
  retrievedCount: number;
  meanRetrievedTokens: number;
  /** Mean of the per-query chance levels — what random selection would score. */
  chanceLevel: number;
  /**
   * True when the top k covers so much of the corpus that the score is a
   * property of the arithmetic rather than of the retrieval.
   */
  degenerate: boolean;
}

/** Results to retrieve at a given chunk size for a fixed context budget. */
export function kFor(budgetTokens: number, chunkSize: number): number {
  return Math.max(1, Math.floor(budgetTokens / Math.max(1, chunkSize)));
}

/** True when a chunk shares at least one character with a marked span. */
export function touches(chunk: ChunkSpec, spans: readonly AnswerSpan[]): boolean {
  for (const s of spans) {
    if (s.docId !== chunk.docId) continue;
    if (s.start < chunk.end && s.end > chunk.start) return true;
  }
  return false;
}

/**
 * The probability that k chunks drawn uniformly at random from n include at
 * least one of the h that touch the marked span: 1 - C(n-h, k) / C(n, k).
 *
 * Computed as a running product rather than with factorials, which overflow
 * immediately at these sizes.
 */
export function chanceOfHit(n: number, h: number, k: number): number {
  if (n <= 0 || h <= 0) return 0;
  // Drawing nothing hits nothing, and this has to be settled BEFORE the h >= n
  // shortcut below: with every chunk touching the span that shortcut would
  // otherwise return 1 for a k of 0, i.e. certainty from an empty draw. The
  // running product gets this right on its own — the early returns are the only
  // place the k = 0 column can go wrong.
  if (k <= 0) return 0;
  if (k >= n) return 1;
  if (h >= n) return 1;
  let miss = 1;
  for (let i = 0; i < k; i++) {
    const remaining = n - i;
    const nonTouching = n - h - i;
    if (nonTouching <= 0) return 1;
    miss *= nonTouching / remaining;
  }
  return 1 - miss;
}

export function scoreQuery(
  docs: readonly IndexedDoc[],
  chunks: readonly ChunkSpec[],
  ranked: readonly { i: number; score: number }[],
  query: QuerySpec,
  k: number,
): QueryScore {
  const top = ranked.slice(0, k);
  const docOf = (id: number) => docs.find((d) => d.id === id);

  let retrievedTokens = 0;
  const hits: Hit[] = [];
  let firstHitRank: number | null = null;
  const marked = query.spans.length > 0;

  for (let r = 0; r < top.length; r++) {
    const c = chunks[top[r].i];
    const doc = docOf(c.docId);
    if (!doc) continue;
    retrievedTokens += tokensIn(doc.tokens, c.start, c.end);
    const isHit = marked && touches(c, query.spans);
    if (isHit && firstHitRank === null) firstHitRank = r + 1;
    hits.push({ chunk: top[r].i, score: top[r].score, isHit });
  }

  const reachableChunks = marked ? chunks.filter((c) => touches(c, query.spans)).length : 0;

  return {
    queryId: query.id,
    marked,
    hits,
    retrieved: firstHitRank !== null,
    firstHitRank,
    retrievedTokens,
    chunkCount: chunks.length,
    reachableChunks,
    chanceLevel: marked ? chanceOfHit(chunks.length, reachableChunks, Math.min(k, top.length || k)) : 0,
  };
}

export function summarise(
  strategy: string,
  size: number,
  overlap: number,
  k: number,
  chunks: readonly ChunkSpec[],
  perQuery: QueryScore[],
): RunScore {
  const counts = chunks.map((c) => c.tokenCount).sort((a, b) => a - b);
  const mid = counts.length >> 1;
  const medianTokens =
    counts.length === 0 ? 0 : counts.length % 2 ? counts[mid] : Math.round((counts[mid - 1] + counts[mid]) / 2);
  const marked = perQuery.filter((q) => q.marked);
  return {
    strategy,
    size,
    overlap,
    k,
    chunkCount: chunks.length,
    oversized: chunks.filter((c) => c.tokenCount > size).length,
    medianTokens,
    perQuery,
    markedCount: marked.length,
    retrievedCount: marked.filter((q) => q.retrieved).length,
    meanRetrievedTokens: marked.length ? marked.reduce((a, q) => a + q.retrievedTokens, 0) / marked.length : 0,
    chanceLevel: marked.length ? marked.reduce((a, q) => a + q.chanceLevel, 0) / marked.length : 0,
    // Retrieving a third of the corpus is not retrieval. The threshold is
    // deliberately generous: at half the corpus in the top k there is nothing
    // left to measure.
    degenerate: chunks.length > 0 && k >= chunks.length * 0.5,
  };
}

/**
 * The cheapest setting that returned everything marked.
 *
 * This is deliberately a DESCRIPTION of what happened, not a claim that the
 * setting is better than the runners-up. Separating two close settings takes
 * around six queries that disagree one way; with the two to five queries anyone
 * actually types, most pairs tie exactly and a meaningful fraction come out
 * backwards. So the interface reports what was observed, names the number of
 * queries it rests on, and does not print a winner.
 *
 * "Cheapest" is measured in retrieved tokens, because that is what a pipeline
 * pays per query and it is what keeps a bigger-is-always-safer answer honest.
 * Degenerate settings — where the top k is half the corpus — are excluded
 * outright rather than allowed to win by covering everything.
 */
export function recommend(runs: readonly RunScore[]): { best: RunScore | null; complete: boolean } {
  const scored = runs.filter((r) => r.markedCount > 0 && !r.degenerate);
  if (scored.length === 0) return { best: null, complete: false };
  const perfect = scored.filter((r) => r.retrievedCount === r.markedCount);
  if (perfect.length > 0) {
    const best = [...perfect].sort((a, b) => a.meanRetrievedTokens - b.meanRetrievedTokens)[0];
    return { best, complete: true };
  }
  const best = [...scored].sort(
    (a, b) => b.retrievedCount - a.retrievedCount || a.meanRetrievedTokens - b.meanRetrievedTokens,
  )[0];
  return { best, complete: false };
}

/**
 * How many queries separate two settings, one way.
 *
 * Reported rather than turned into a verdict: below about six one-way
 * disagreements an exact paired sign test cannot reach p < 0.05 at all, so a
 * confident-sounding comparison of two settings on three queries is a coin toss
 * wearing a percentage.
 */
export function discordance(a: RunScore, b: RunScore): { aOnly: number; bOnly: number; tied: number } {
  let aOnly = 0;
  let bOnly = 0;
  let tied = 0;
  for (const qa of a.perQuery) {
    if (!qa.marked) continue;
    const qb = b.perQuery.find((q) => q.queryId === qa.queryId);
    if (!qb || !qb.marked) continue;
    if (qa.retrieved === qb.retrieved) tied += 1;
    else if (qa.retrieved) aOnly += 1;
    else bOnly += 1;
  }
  return { aOnly, bOnly, tied };
}
